import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { performSignOut } from '@/features/auth/lib/sign-out';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Live CSS var written by this banner (0px when idle). Overlays that
 * use `z-modal` (below `z-max`) must offset by this height so the
 * banner stays visible without covering the dialog.
 */
export const IMPERSONATION_BANNER_HEIGHT_VAR = '--impersonation-banner-height';

const ROLE_LABELS: Record<string, string> = {
  SSIAdministrator:    'SSI Administrator',
  SSIEmployee:         'SSI Employee',
  CompanyAdministrator: 'Company Admin',
  CompanyManager:       'Company Manager',
  CompanyUser:          'Company User',
  LimitedUser:          'Limited User',
  ReadOnly:             'Limited User',
};

/**
 * PRD §11/07 §4.3 — persistent banner shown on every page during an
 * impersonation session. Reads from `realUser` (set by the post-login
 * handoff) so staff always know whose identity they are operating as.
 *
 * Exit Impersonation fully signs out (Supabase + API + store) and sends
 * the operator to /login — BR-IMP-010 (SSI must re-authenticate).
 */
export function ImpersonationBanner() {
  const realUser = useAuthStore((s) => s.realUser);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [exiting, setExiting] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = bannerRef.current;
    if (!el) return;

    const syncHeight = () => {
      document.documentElement.style.setProperty(
        IMPERSONATION_BANNER_HEIGHT_VAR,
        `${el.offsetHeight}px`,
      );
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty(IMPERSONATION_BANNER_HEIGHT_VAR, '0px');
    };
  }, [realUser, user]);

  if (!realUser || !user) return null;

  const roleLabel = ROLE_LABELS[user.role] ?? user.role;

  async function onExit() {
    if (exiting) return;
    setExiting(true);
    try {
      await performSignOut({ exitImpersonation: true });
      queryClient.clear();
      navigate('/login', { replace: true });
    } finally {
      setExiting(false);
    }
  }

  return (
    <div
      ref={bannerRef}
      role="status"
      aria-live="polite"
      data-testid="impersonation-banner"
      className="fixed inset-x-0 top-0 z-max flex items-center justify-center gap-3 bg-warning-bg px-4 py-2 text-sm font-medium text-warning shadow-md"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
      <span>
        Impersonating{' '}
        <strong>
          {user.firstName} {user.lastName}
        </strong>{' '}
        ({roleLabel})
        {user.companyName ? <span className="opacity-80"> · {user.companyName}</span> : null}
      </span>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={onExit}
        disabled={exiting}
        className="ml-2"
      >
        <X />
        {exiting ? 'Exiting…' : 'Exit Impersonation'}
      </Button>
    </div>
  );
}
